/**
 * Pure geometry builders for vector layers (no Babylon): world-width ribbons along polylines with
 * mitred joins and a per-vertex lateral coordinate (for analytic AA), and ear-clipping
 * triangulation of simple polygons. Output positions are Babylon (x, 0, y) triples.
 */

export interface MeshArrays {
  positions: Float32Array;
  /** per-vertex signed lateral distance from the centre line (m); empty for fills */
  lateral: Float32Array;
  indices: Uint32Array;
}

type Pt = readonly number[];

/** Drop consecutive duplicates (and the closing duplicate of a closed ring). */
export function cleanPolyline(pts: readonly Pt[], closed: boolean): [number, number][] {
  const out: [number, number][] = [];
  for (const p of pts) {
    const x = p[0] ?? 0;
    const y = p[1] ?? 0;
    const last = out[out.length - 1];
    if (last && Math.abs(last[0] - x) < 1e-9 && Math.abs(last[1] - y) < 1e-9) continue;
    out.push([x, y]);
  }
  if (closed && out.length > 2) {
    const a = out[0];
    const b = out[out.length - 1];
    if (a && b && Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9) out.pop();
  }
  return out;
}

/**
 * Append a ribbon of half-width `halfW` (+ `pad` metres of AA fringe) along a polyline.
 * Mitred joins, miter length limited to `miterLimit` × half-width (beyond that the join is
 * clamped, which slightly narrows very sharp corners).
 */
export function appendRibbon(
  pts: readonly Pt[],
  closed: boolean,
  halfW: number,
  pad: number,
  pos: number[],
  lat: number[],
  idx: number[],
  miterLimit = 4,
): void {
  const p = cleanPolyline(pts, closed);
  const n = p.length;
  if (n < 2) return;
  const base = pos.length / 3;
  const h = halfW + pad;
  for (let i = 0; i < n; i++) {
    const cur = p[i] ?? [0, 0];
    const prev = closed ? p[(i - 1 + n) % n] : p[i - 1];
    const next = closed ? p[(i + 1) % n] : p[i + 1];
    let t0x = 0;
    let t0y = 0;
    let t1x = 0;
    let t1y = 0;
    if (prev) {
      const dx = cur[0] - prev[0];
      const dy = cur[1] - prev[1];
      const l = Math.hypot(dx, dy) || 1;
      t0x = dx / l;
      t0y = dy / l;
    }
    if (next) {
      const dx = next[0] - cur[0];
      const dy = next[1] - cur[1];
      const l = Math.hypot(dx, dy) || 1;
      t1x = dx / l;
      t1y = dy / l;
    }
    if (!prev) {
      t0x = t1x;
      t0y = t1y;
    }
    if (!next) {
      t1x = t0x;
      t1y = t0y;
    }
    let tx = t0x + t1x;
    let ty = t0y + t1y;
    let tl = Math.hypot(tx, ty);
    if (tl < 1e-9) {
      tx = t0x;
      ty = t0y;
      tl = 1;
    }
    tx /= tl;
    ty /= tl;
    // left normal of the bisector tangent; miter scale 1 / cos(half angle)
    const mx = -ty;
    const my = tx;
    const cosHalf = Math.max(1 / miterLimit, mx * -t0y + my * t0x);
    const s = h / cosHalf;
    pos.push(cur[0] + mx * s, 0, cur[1] + my * s, cur[0] - mx * s, 0, cur[1] - my * s);
    lat.push(s, -s);
  }
  const segs = closed ? n : n - 1;
  for (let i = 0; i < segs; i++) {
    const a = base + i * 2;
    const b = base + ((i + 1) % n) * 2;
    idx.push(a, b, a + 1, a + 1, b, b + 1);
  }
}

/** Signed area (positive = counter-clockwise in a y-up frame). */
export function signedArea(p: readonly (readonly [number, number])[]): number {
  let a = 0;
  for (let i = 0; i < p.length; i++) {
    const u = p[i] ?? [0, 0];
    const v = p[(i + 1) % p.length] ?? [0, 0];
    a += u[0] * v[1] - v[0] * u[1];
  }
  return a / 2;
}

function pointInTri(px: number, py: number, a: readonly number[], b: readonly number[], c: readonly number[]): boolean {
  const ax = a[0] ?? 0;
  const ay = a[1] ?? 0;
  const bx = b[0] ?? 0;
  const by = b[1] ?? 0;
  const cx = c[0] ?? 0;
  const cy = c[1] ?? 0;
  const d1 = (px - bx) * (ay - by) - (ax - bx) * (py - by);
  const d2 = (px - cx) * (by - cy) - (bx - cx) * (py - cy);
  const d3 = (px - ax) * (cy - ay) - (cx - ax) * (py - ay);
  const neg = d1 < 0 || d2 < 0 || d3 < 0;
  const pos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(neg && pos);
}

/**
 * Ear-clipping triangulation of a simple polygon (no holes). Returns indices into the cleaned
 * ring (see `cleanPolyline(pts, true)`), counter-clockwise triangles. O(n²), fine for OSM shapes.
 */
export function triangulate(ring: readonly (readonly [number, number])[]): number[] {
  const n = ring.length;
  if (n < 3) return [];
  const ccw = signedArea(ring) > 0;
  const V: number[] = [];
  for (let i = 0; i < n; i++) V.push(ccw ? i : n - 1 - i);
  const out: number[] = [];
  let guard = 0;
  while (V.length > 3 && guard++ < n * n) {
    let clipped = false;
    for (let i = 0; i < V.length; i++) {
      const ia = V[(i - 1 + V.length) % V.length] ?? 0;
      const ib = V[i] ?? 0;
      const ic = V[(i + 1) % V.length] ?? 0;
      const a = ring[ia] ?? [0, 0];
      const b = ring[ib] ?? [0, 0];
      const c = ring[ic] ?? [0, 0];
      const cross = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
      if (cross <= 1e-12) continue; // reflex or degenerate
      let inside = false;
      for (const j of V) {
        if (j === ia || j === ib || j === ic) continue;
        const q = ring[j] ?? [0, 0];
        if (pointInTri(q[0], q[1], a, b, c)) {
          inside = true;
          break;
        }
      }
      if (inside) continue;
      out.push(ia, ib, ic);
      V.splice(i, 1);
      clipped = true;
      break;
    }
    if (!clipped) {
      // not simple / numerically stuck: fan the rest so the shape still renders
      for (let i = 1; i + 1 < V.length; i++) out.push(V[0] ?? 0, V[i] ?? 0, V[i + 1] ?? 0);
      return out;
    }
  }
  if (V.length === 3) out.push(V[0] ?? 0, V[1] ?? 0, V[2] ?? 0);
  return out;
}

/** Append a filled polygon (triangulated) to the arrays. */
export function appendPolygon(pts: readonly Pt[], pos: number[], idx: number[]): void {
  const ring = cleanPolyline(pts, true);
  if (ring.length < 3) return;
  const base = pos.length / 3;
  for (const p of ring) pos.push(p[0], 0, p[1]);
  for (const i of triangulate(ring)) idx.push(base + i);
}

/** Total area of a triangle list (for tests). */
export function trianglesArea(pos: ArrayLike<number>, idx: ArrayLike<number>): number {
  let a = 0;
  for (let t = 0; t + 2 < idx.length; t += 3) {
    const i = (idx[t] ?? 0) * 3;
    const j = (idx[t + 1] ?? 0) * 3;
    const k = (idx[t + 2] ?? 0) * 3;
    const ax = pos[i] ?? 0;
    const ay = pos[i + 2] ?? 0;
    const bx = pos[j] ?? 0;
    const by = pos[j + 2] ?? 0;
    const cx = pos[k] ?? 0;
    const cy = pos[k + 2] ?? 0;
    a += Math.abs((bx - ax) * (cy - ay) - (by - ay) * (cx - ax)) / 2;
  }
  return a;
}
